import React from "react";
import { useState, useEffect } from "react";
import { Lock, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { Card } from "@/components/ui/card";
import { useUser } from "@/hooks/useUser";
import { useSetAtom, useAtom } from "jotai";
import { registerDialog } from "@/state/dialogs";
import { Logo } from "@/components/ui/logo";
import { IOS_PRICING, getTranslatedPricing } from "@/config/pricing";
import { LoadingButton } from "@/components/ui/loading-button";
import { useIsIOS } from "@/hooks/useIsIOS";
import { useIsAndroid } from "@/hooks/useIsAndroid";
import { ResponsiveDialogDrawer } from "@/components/sheets/ResponsiveDialogDrawer";
import { useIAP } from "@/providers/IAPProvider";
import { usePlayBilling } from "@/providers/PlayBillingProvider";
import type { IOSProduct } from "@/lib/types";
import {
  getPriceDisplayIOS,
  getPriceDisplayWeb,
  getPriceDisplayAndroid,
  calculateYearlyDiscount,
  calculateDailyPrice,
} from "./Pro.helpers";
import { useNotification } from "@/hooks/useNotification";
import { useTranslation } from "@/hooks/useTranslation";
import { subscription } from "@/lib/auth-client";
import { Heading } from "@/components/ui/heading";
import { selectedPlanAtom } from "@/state/payment";

type ProProps = {
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  onboarding?: boolean;
  onCompleted?: () => void;
};

export const Pro = ({ open, onOpenChange, onboarding = false, onCompleted }: ProProps) => {
  const isIOS = useIsIOS();
  const isAndroid = useIsAndroid();
  const { isAuthenticated, userId, user } = useUser();
  const { IOSproducts, purchaseIOSProduct } = useIAP();
  const { purchaseSubscription, paymentStatus, products } = usePlayBilling();
  const [selectedPlan, setSelectedPlan] = useAtom(selectedPlanAtom);
  const setRegisterDialogOpen = useSetAtom(registerDialog);
  const [monthlyProduct, setMonthlyProduct] = useState<IOSProduct | null>(null);
  const [yearlyProduct, setYearlyProduct] = useState<IOSProduct | null>(null);
  const { showNotification } = useNotification();
  const { t } = useTranslation();
  const [isPending, setIsPending] = useState(false);
  const translatedPricing = getTranslatedPricing(t);

  useEffect(() => {
    if (IOSproducts.length > 0) {
      const monthly = IOSproducts.find(
        (p) => p.attributes.offerName === IOS_PRICING.monthly_subscription,
      );
      const yearly = IOSproducts.find(
        (p) => p.attributes.offerName === IOS_PRICING.yearly_subscription,
      );
      setMonthlyProduct(monthly || null);
      setYearlyProduct(yearly || null);
    }
  }, [IOSproducts]);

  const createStripeCheckoutSession = async ({ isYearly }: { isYearly: boolean }) => {
    setIsPending(true);
    const { error } = await subscription.upgrade({
      plan: isYearly ? "yearly" : "monthly",
      successUrl: "/dashboard?subscriptionSuccess=true",
      cancelUrl: "/dashboard?subscriptionCanceled=true",
    });

    if (error) {
      showNotification({
        type: "error",
        message: t("pro.error_creating_checkout"),
        description: error.message,
      });
      console.error("Error creating checkout session:", error);
    }
    setIsPending(false);
  };

  const handlePlanSelection = (e: React.TouchEvent | React.MouseEvent, yearly: boolean) => {
    setSelectedPlan(yearly ? "yearly" : "monthly");
  };

  const handleUpgrade = async () => {
    if (!isAuthenticated || !userId) {
      setRegisterDialogOpen(true);
      onOpenChange?.(false);
      return;
    }

    if (isIOS) {
      const product = selectedPlan === "yearly" ? yearlyProduct : monthlyProduct;
      if (product) {
        purchaseIOSProduct(product.attributes.offerName);
      }
    } else if (isAndroid) {
      // Use Google Play Billing for Android devices
      const success = await purchaseSubscription(selectedPlan);
      if (success) {
        // Close dialog and show success state
        onOpenChange?.(false);
        onCompleted?.();
      }
    } else {
      // Use Stripe for web/desktop
      await createStripeCheckoutSession({ isYearly: selectedPlan === "yearly" });
    }
  };

  const handleRestore = async () => {
    if (!isAuthenticated) {
      setRegisterDialogOpen(true);
      onOpenChange?.(false);
      return;
    }

    onOpenChange?.(false);
    showNotification({
      type: "info",
      message: t("pro.no_purchases_to_restore"),
    });
  };

  const monthlyPriceDisplay = isIOS
    ? getPriceDisplayIOS(false, yearlyProduct, monthlyProduct)
    : isAndroid && products
      ? getPriceDisplayAndroid(false, products, t)
      : getPriceDisplayWeb(false);

  const yearlyPriceDisplay = isIOS
    ? getPriceDisplayIOS(true, yearlyProduct, monthlyProduct)
    : isAndroid && products
      ? getPriceDisplayAndroid(true, products, t)
      : getPriceDisplayWeb(true);

  const yearlyDiscount = calculateYearlyDiscount(
    Number(monthlyPriceDisplay.price.replace(/[^0-9.-]+/g, "")),
    Number(yearlyPriceDisplay.monthlyPrice?.replace(/[^0-9.-]+/g, "") || "0"),
  );

  const cardContent = (
    <>
      {/* Header */}
      <div className="mb-8 text-center relative">
        {onboarding && (
          <button
            type="button"
            onClick={() => {
              onOpenChange?.(false);
              onCompleted?.();
            }}
            className="md:hidden absolute right-0 top-0 p-2 hover:bg-muted rounded-full transition-colors"
          >
            <X className="size-5" />
          </button>
        )}
        <div className="mb-4 flex justify-center">
          <Logo className="size-10" iconClassName="size-8" />
        </div>
        <Heading level="h2" size="h3" className="mb-2">
          {t("pro.title")}
        </Heading>
        <p className="text-muted-foreground">
          {user?.name
            ? t("pro.subtitle_personalized", {
                name: user.name
                  .split(" ")
                  .map((name) => name.charAt(0).toUpperCase() + name.slice(1))
                  .join(" "),
              })
            : t("pro.subtitle")}
        </p>
      </div>

      {/* Main content */}
      <div className="grid gap-8 md:grid-cols-2">
        {/* Features list */}
        <div className="space-y-4">
          {translatedPricing.pro.features.slice(1).map((feature) => (
            <div key={feature.text} className="flex items-center gap-3">
              <div className="rounded-full bg-primary/10 p-1 hover:bg-primary/20 transition-colors">
                {React.createElement(feature.icon, { className: "size-4 text-primary" })}
              </div>
              <span className="text-sm">{feature.text}</span>
            </div>
          ))}
        </div>

        {/* Pricing section */}
        <div className="space-y-4 pr-0.5">
          {/* Monthly option */}
          <Card
            className={cn(
              "p-4 cursor-pointer relative overflow-hidden border",
              selectedPlan === "monthly"
                ? "border-primary bg-primary/5 shadow-[0_0_15px_rgba(251,191,36,0.1)] ring-1 ring-primary"
                : "bg-card hover:border-primary/50",
            )}
            onClick={(e) => handlePlanSelection(e, false)}
            onTouchStart={(e) => handlePlanSelection(e, false)}
          >
            <div className="flex items-center justify-between">
              <div className="space-y-1">
                <p className="text-sm font-medium">{t("pro.monthly")}</p>
              </div>
              <div className="text-right">
                <span className="text-lg font-bold">{monthlyPriceDisplay.price}</span>
                <span className="text-sm text-muted-foreground">
                  {" "}
                  / {monthlyPriceDisplay.period}
                </span>
              </div>
            </div>
          </Card>

          {/* Yearly option */}
          <Card
            className={cn(
              "p-4 cursor-pointer relative border",
              selectedPlan === "yearly"
                ? "border-primary bg-primary/5 shadow-[0_0_15px_rgba(251,191,36,0.1)] ring-1 ring-primary"
                : "bg-card hover:border-primary/50",
            )}
            onClick={(e) => handlePlanSelection(e, true)}
            onTouchStart={(e) => handlePlanSelection(e, true)}
          >
            <div className="absolute -top-3.5 left-1/2 -translate-x-1/2">
              <span className="inline-block rounded-full bg-primary px-3 py-0.5 text-xs font-bold text-primary-foreground shadow-xs">
                {t("pro.save_percentage", { percentage: yearlyDiscount })}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <div className="space-y-1">
                <p className="text-sm font-medium">{t("pro.yearly")}</p>
              </div>
              <div className="text-right flex flex-col">
                <div>
                  <span className="text-lg font-bold">{yearlyPriceDisplay.price}</span>
                  <span className="text-sm text-muted-foreground">
                    {" "}
                    / {yearlyPriceDisplay.period}
                  </span>
                </div>
                {yearlyPriceDisplay.monthlyPrice && (
                  <span className="text-xs text-muted-foreground">
                    {t("pro.billed_annually", { price: yearlyPriceDisplay.monthlyPrice })}
                  </span>
                )}
              </div>
            </div>
          </Card>

          <div className="text-center text-sm text-muted-foreground">
            {selectedPlan === "yearly"
              ? t("pro.per_day", { price: calculateDailyPrice(yearlyPriceDisplay.price, true) })
              : t("pro.per_day", { price: calculateDailyPrice(monthlyPriceDisplay.price, false) })}
          </div>

          <div className="flex items-center justify-center gap-2 text-sm mt-4">
            <Lock className="size-4" />
            <span>
              {t("pro.secure_payment", {
                provider: isIOS ? "Apple Pay" : isAndroid ? "Google Play" : "Stripe",
              })}
            </span>
          </div>

          <LoadingButton
            data-umami-event="pro-upgrade-button"
            data-testid="pro-upgrade-button"
            onClick={handleUpgrade}
            className="w-full bg-linear-to-r from-primary to-primary/80 hover:from-primary/90 hover:to-primary/70 truncate"
            size="lg"
            loading={isPending || paymentStatus === "loading"}
          >
            {isIOS
              ? t("pro.continue_with_apple_pay")
              : isAndroid
                ? t("pro.continue_with_google_play")
                : t("pro.continue_with_stripe")}
          </LoadingButton>

          {isIOS && (
            <LoadingButton
              data-umami-event="pro-restore-button"
              data-testid="pro-restore-button"
              onClick={handleRestore}
              variant="ghost"
              className="w-full"
              size="sm"
            >
              {t("pro.restore_purchases")}
            </LoadingButton>
          )}
        </div>
      </div>
    </>
  );

  if (onboarding) {
    return cardContent;
  }

  return (
    <ResponsiveDialogDrawer
      open={open ?? false}
      onOpenChange={onOpenChange ?? (() => {})}
      className="max-w-xl"
    >
      {cardContent}
    </ResponsiveDialogDrawer>
  );
};
